/**
 * Comenzile plasate de pe acest dispozitiv.
 *
 * Platforma nu are conturi, deci „istoricul" e ce a vazut browserul asta: codul
 * si telefonul fiecarei comenzi (necesare urmaririi), plus — pentru cele
 * plasate de la v2.1 — ce s-a comandat, de unde si cat. Lista e locala si se
 * spune asta: pe alt telefon nu apare.
 *
 * Starea fiecarei comenzi se cere de la server, DOAR pentru cele recente si
 * neterminate: o comanda livrata acum trei saptamani nu se mai schimba, iar
 * douazeci de cereri la deschiderea paginii ar fi munca pentru nimic.
 */

import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

import { fetchOrderTracking } from "../api/client";
import { useBootstrap } from "../components/BootstrapProvider";
import { cartLinesFromRemembered, readRememberedOrders, useCart, type RememberedOrder } from "../components/CartProvider";
import { IconChevronRight, IconRepeat } from "../components/icons";
import { orderStatusLabel } from "../components/OrderTimeline";
import { EmptyState } from "../components/StateScreen";
import { useToast } from "../components/ToastProvider";
import { Button, Price, SectionHeading } from "../components/ui";
import { useLoadable } from "../components/useLoadable";

const LIVE_STATUS_LOOKUPS = 5;

function formatDay(iso: string | null | undefined, timezone: string): string | null {
  if (typeof iso !== "string" || iso.length === 0) return null;
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return null;
  try {
    return new Intl.DateTimeFormat("ro-RO", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: timezone }).format(value);
  } catch {
    return new Intl.DateTimeFormat("ro-RO", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(value);
  }
}

function OrderRow({ order, status, onReorder }: { order: RememberedOrder; status: string | null; onReorder: (() => void) | null }) {
  const { config } = useBootstrap();
  const lines = order.lines ?? [];
  const summary = lines.map((line) => `${line.qty}× ${line.name}`).join(", ");
  return (
    <li className="da-card flex flex-col gap-3 p-4">
      <Link to={`/comanda/${encodeURIComponent(order.code)}`} className="da-tap flex items-start gap-3 text-left">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 text-sm">
            <span className="font-extrabold text-ink">{order.code}</span>
            {status === null ? null : <span className="rounded-full bg-surface-raised px-2 py-0.5 text-xs font-semibold text-ink">{orderStatusLabel(status)}</span>}
          </p>
          <p className="text-sm font-semibold text-ink">{order.storefrontName ?? order.sellerName ?? "Comandă"}</p>
          <p className="text-xs text-ink-muted">
            {[formatDay(order.placedAt, config.hours.timezone), summary].filter((part): part is string => part !== null && part.length > 0).join(" · ")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {typeof order.total === "number" ? <Price amount={order.total} className="text-sm font-bold text-ink" /> : null}
          <IconChevronRight size={18} className="text-ink-muted" />
        </div>
      </Link>
      {onReorder === null ? null : (
        <div>
          <Button size="sm" variant="secondary" icon={<IconRepeat size={16} />} onClick={onReorder}>
            Comandă din nou
          </Button>
        </div>
      )}
    </li>
  );
}

export function Orders() {
  const { marketKey, bootstrap } = useBootstrap();
  const { restoreOrder } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const orders = useMemo(() => readRememberedOrders(marketKey), [marketKey]);
  const storefrontSlugs = useMemo(() => new Set(bootstrap.storefronts.map((storefront) => storefront.slug)), [bootstrap.storefronts]);

  const statuses = useLoadable<Map<string, string>>(
    async (signal) => {
      const map = new Map<string, string>();
      // Doar cele recente; esecul uneia nu opreste restul — lipsa starii se
      // arata ca lipsa, nu ca eroare de pagina.
      await Promise.all(
        orders.slice(0, LIVE_STATUS_LOOKUPS).map(async (order) => {
          try {
            const tracking = await fetchOrderTracking(marketKey, order.code, order.phone, signal);
            map.set(order.code, tracking.status);
          } catch {
            // Se lasa fara stare.
          }
        }),
      );
      return map;
    },
    [marketKey, orders],
  );

  if (orders.length === 0) {
    return (
      <EmptyState
        title="Nu ai comenzi pe acest dispozitiv"
        description="Comenzile plasate de aici apar în lista asta, fără cont. Pe alt telefon le găsești după cod și numărul de telefon."
        action={<Button onClick={() => navigate("/")}>Vezi restaurantele</Button>}
      />
    );
  }

  const reorderFor = (order: RememberedOrder): (() => void) | null => {
    if (typeof order.storefrontSlug !== "string" || !storefrontSlugs.has(order.storefrontSlug)) return null;
    const lines = cartLinesFromRemembered(order.lines ?? []);
    if (lines.length === 0) return null;
    return () => {
      const storefront = bootstrap.storefronts.find((item) => item.slug === order.storefrontSlug) ?? null;
      restoreOrder(order.storefrontSlug as string, storefront?.name ?? order.storefrontName ?? null, lines);
      toast({ key: "reorder", title: "Produsele sunt din nou în coș", description: "Le verificăm la adresa ta înainte de finalizare." });
      navigate("/cos");
    };
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <SectionHeading level={1}>Comenzile mele</SectionHeading>
      <p className="text-sm text-ink-muted">Comenzile plasate de pe acest dispozitiv. Apasă pe una ca să-i vezi starea.</p>
      <ul className="flex flex-col gap-3">
        {orders.map((order) => (
          <OrderRow key={order.code} order={order} status={statuses.data?.get(order.code) ?? null} onReorder={reorderFor(order)} />
        ))}
      </ul>
    </div>
  );
}
